import React from 'react';
import { Check, Edit, Building2, Calendar, MapPin, Wrench } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDate } from '../../utils/dateUtils';

interface ProjectPreviewMessageProps {
  message: string;
  projectPreview: {
    name: string;
    description: string;
    startDate: string;
    siteAddress?: {
      street?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    } | null;
    constructionTypes: string[];
    projectId: string;
    projectNumber: string;
  };
  onConfirm: () => void;
  onEdit: () => void;
}

export const ProjectPreviewMessage: React.FC<ProjectPreviewMessageProps> = ({
  message,
  projectPreview,
  onConfirm,
  onEdit
}) => {
  const { t } = useTranslation();

  const formatAddress = (address: any) => {
    if (!address) return null;
    const parts = [
      address.street,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-800 rounded-lg p-4 shadow-sm">
      <p className="text-xs text-gray-700 dark:text-gray-300 mb-3">{message}</p>
      
      <div className="bg-purple-50 dark:bg-gray-700 dark:border dark:border-purple-800/50 rounded-md p-3 space-y-2 mb-3">
        <div className="flex items-start space-x-2">
          <Building2 className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{t('project.name')}</p>
            <p className="text-xs text-gray-700 dark:text-gray-300">{projectPreview.name}</p>
          </div>
        </div>

        {projectPreview.description && (
          <div className="flex items-start space-x-2">
            <div className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{t('project.description')}</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{projectPreview.description}</p>
            </div>
          </div>
        )}

        {projectPreview.startDate && (
          <div className="flex items-start space-x-2">
            <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{t('project.startDate')}</p>
              <p className="text-xs text-gray-700 dark:text-gray-300">
                {formatDate(projectPreview.startDate)}
              </p>
            </div>
          </div>
        )}

        {projectPreview.siteAddress && formatAddress(projectPreview.siteAddress) && (
          <div className="flex items-start space-x-2">
            <MapPin className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{t('project.siteAddress')}</p>
              <p className="text-xs text-gray-700 dark:text-gray-300">
                {formatAddress(projectPreview.siteAddress)}
              </p>
            </div>
          </div>
        )}

        {projectPreview.constructionTypes && projectPreview.constructionTypes.length > 0 && (
          <div className="flex items-start space-x-2">
            <Wrench className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{t('project.constructionTypes')}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {projectPreview.constructionTypes.map((type, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={onConfirm}
          className="flex-1 px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-xs flex items-center justify-center space-x-1"
        >
          <Check className="w-3 h-3" />
          <span>{t('project.confirmProject')}</span>
        </button>
        <button
          onClick={onEdit}
          className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-xs flex items-center justify-center space-x-1"
        >
          <Edit className="w-3 h-3" />
          <span>{t('project.editProject')}</span>
        </button>
      </div>
    </div>
  );
};

